import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Empty, Space, Spin, Tooltip, Typography } from 'antd';
import { FileExclamationOutlined, ReloadOutlined } from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import { H2 } from 'components/new-ui/base/typography/styles';
import { Ics2CustomsRequest, Ics2Referral, Ics2ReferralDraft } from 'store/declarations/eu/ics2/customs-requests/types';
import { getRequestIssueDate } from 'store/declarations/eu/ics2/customs-requests/utils';
import ReferralDrawer from './ReferralDrawer';
import RequestCard from './RequestCard';
import { CustomsRequestsLayout, PageHeader } from '../styles';
import useIcs2CodelistLabel from 'hooks/useIcs2CodelistLabel';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { describeDocumentRequest, needsEvidenceConfirmation } from './evidenceExpectation';
import { Ics2DocumentsByReferral, Ics2ReferralRow, getReferralAttachments } from './referralDocuments';
import { getDraftErrors } from './referralSending';
import { Ics2SendOutcome } from './responseForm';
import useIcs2CustomsRequests from './hooks/useIcs2CustomsRequests';
import usePostcodeOptionalCountries from './hooks/usePostcodeOptionalCountries';

type MissingEvidence = { reference: string; requested?: string };

/**
 * Every referral Customs has raised against this ENS filing.
 *
 * One IE3Q02/IE3Q03 carries 1..999 referrals, the risk-analysis loop is re-entrant, and a new
 * request can land even after Assessment Complete - so the tab lists them all rather than
 * surfacing only the newest message.
 */
const CustomsRequests = () => {
    const { declarationId } = useParams<{ declarationId?: string }>();
    const {
        requests,
        rows,
        dispatchErrors,
        documentsByReferral,
        isLoading,
        isRefreshing,
        loadError,
        isSubmitting,
        reload,
        refreshDocuments,
        readLatestDocuments,
        saveDraft,
        saveAndSubmit,
        submitSelection,
    } = useIcs2CustomsRequests(declarationId);

    const getLabel = useIcs2CodelistLabel([ICS2CodelistsMap.CL013]);
    const postcodeOptionalCountries = usePostcodeOptionalCountries();

    const draftErrors = useMemo(
        () =>
            rows.reduce<Record<string, string[]>>((all, row) => {
                all[row.referral.id] = getDraftErrors(row, {
                    postcodeOptionalCountries,
                    documents: documentsByReferral,
                });
                return all;
            }, {}),
        [rows, postcodeOptionalCountries, documentsByReferral]
    );

    const [expandedRequestIds, setExpandedRequestIds] = useState<string[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [sendingRequestId, setSendingRequestId] = useState<string>();
    const [openReferralId, setOpenReferralId] = useState<string>();
    const [pendingConfirmation, setPendingConfirmation] = useState<{
        answers: MissingEvidence[];
        decide: (proceed: boolean) => void;
    } | null>(null);

    const toggleIn = (ids: string[], id: string) =>
        ids.includes(id) ? ids.filter((candidate) => candidate !== id) : [...ids, id];

    const orderedRequests = useMemo(
        () =>
            [...requests].sort((left, right) => {
                const at = (request: Ics2CustomsRequest) => getRequestIssueDate(request) ?? request.receivedAt ?? '';
                return at(right).localeCompare(at(left));
            }),
        [requests]
    );

    const openRow = rows.find((row) => row.referral.id === openReferralId);

    const withinRequest = (current: string[], request: Ics2CustomsRequest) =>
        current.filter((id) => request.referrals.some((referral) => referral.id === id));

    const toggleSelected = (request: Ics2CustomsRequest, referral: Ics2Referral) =>
        setSelectedIds((current) => toggleIn(withinRequest(current, request), referral.id));

    const handleSave = (request: Ics2CustomsRequest, referral: Ics2Referral, draft: Ics2ReferralDraft) =>
        saveDraft(request, referral, draft);

    const findAnswersWithoutEvidence = (
        entries: Array<{ row: Ics2ReferralRow; draft?: Ics2ReferralDraft }>,
        documents: Ics2DocumentsByReferral
    ) =>
        entries
            .filter(({ row, draft }) =>
                needsEvidenceConfirmation({
                    draft: draft ?? row.referral.responseDraft,
                    attachmentCount: getReferralAttachments(documents, row).length,
                })
            )
            .map(({ row }) => ({
                reference: row.referral.referralRequestReference,
                requested: describeDocumentRequest(row.referral, (type) => getLabel(type, ICS2CodelistsMap.CL013)),
            }));

    const confirmSendWithoutEvidence = (answers: MissingEvidence[]) =>
        new Promise<boolean>((decide) => setPendingConfirmation({ answers, decide }));

    const answer = (proceed: boolean) => {
        pendingConfirmation?.decide(proceed);
        setPendingConfirmation(null);
    };

    const handleSaveAndSend = async (
        request: Ics2CustomsRequest,
        referral: Ics2Referral,
        draft: Ics2ReferralDraft
    ): Promise<Ics2SendOutcome> => {
        // Read once per send: a file just uploaded in the drawer is not in the page's copy yet.
        const documents = await readLatestDocuments();
        const missing = findAnswersWithoutEvidence([{ row: { request, referral }, draft }], documents);
        if (missing.length && !(await confirmSendWithoutEvidence(missing))) return 'cancelled';

        const sent = await saveAndSubmit(request, referral, draft, { postcodeOptionalCountries, documents });
        if (sent) {
            setSelectedIds((ids) => ids.filter((id) => id !== referral.id));
            setOpenReferralId(undefined);
        }
        return sent ? 'sent' : 'failed';
    };

    const handleSend = async (request: Ics2CustomsRequest) => {
        const ids = request.referrals.map((referral) => referral.id).filter((id) => selectedIds.includes(id));
        const selected = rows.filter((row) => ids.includes(row.referral.id));
        const documents = await readLatestDocuments();
        const missing = findAnswersWithoutEvidence(
            selected.map((row) => ({ row })),
            documents
        );
        if (missing.length && !(await confirmSendWithoutEvidence(missing))) return;

        setSendingRequestId(request.id);
        try {
            const sent = await submitSelection(ids, { postcodeOptionalCountries, documents });
            if (sent) setSelectedIds((current) => current.filter((id) => !ids.includes(id)));
        } finally {
            setSendingRequestId(undefined);
        }
    };

    if (isLoading) return <Spin />;
    if (loadError) return <Alert type="warning" showIcon message={loadError} />;

    if (!requests.length) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Customs has not raised any requests against this declaration."
            />
        );
    }

    return (
        <CustomsRequestsLayout>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <PageHeader>
                    <div>
                        <H2>Customs Requests</H2>
                        <Typography.Text type="secondary">
                            Customs does not acknowledge referral responses. A sent response stays sent unless a
                            validation error is returned.
                        </Typography.Text>
                    </div>
                    <Tooltip title="Check for new requests and responses from Customs">
                        <Button
                            icon={<ReloadOutlined spin={isRefreshing} />}
                            loading={isRefreshing}
                            disabled={isSubmitting}
                            onClick={reload}
                        >
                            Refresh
                        </Button>
                    </Tooltip>
                </PageHeader>

                {orderedRequests.map((request) => (
                    <RequestCard
                        key={request.id}
                        request={request}
                        isExpanded={expandedRequestIds.includes(request.id)}
                        selectedIds={selectedIds}
                        draftErrors={draftErrors}
                        isSending={sendingRequestId === request.id}
                        isSubmitting={isSubmitting}
                        onToggleExpanded={() => setExpandedRequestIds((current) => toggleIn(current, request.id))}
                        onToggleSelected={(referral) => toggleSelected(request, referral)}
                        onOpenReferral={(referral) => setOpenReferralId(referral.id)}
                        onSend={() => handleSend(request)}
                    />
                ))}
            </Space>

            {openRow && (
                <ReferralDrawer
                    key={openRow.referral.id}
                    declarationId={declarationId}
                    request={openRow.request}
                    referral={openRow.referral}
                    onSave={handleSave}
                    onSaveAndSend={handleSaveAndSend}
                    dispatchError={dispatchErrors[openRow.referral.id]}
                    onClose={() => {
                        setOpenReferralId(undefined);
                        // The drawer tracks its own uploads; the cards only see them once this runs.
                        refreshDocuments();
                    }}
                />
            )}

            <ConfirmationModal
                title="Send without any attached documents?"
                type="warning"
                icon={<FileExclamationOutlined />}
                maskClosable={false}
                open={Boolean(pendingConfirmation)}
                onConfirm={() => answer(true)}
                onCancel={() => answer(false)}
                confirmButtonText="Send anyway"
                cancelButtonText="Go back and attach"
                content={
                    <>
                        <ul
                            style={{
                                width: '100%',
                                margin: 0,
                                padding: 0,
                                paddingLeft: '1.25em',
                            }}
                        >
                            {(pendingConfirmation?.answers ?? []).map(({ reference, requested }) => (
                                <li key={reference}>
                                    <Typography.Text strong>{reference}</Typography.Text>
                                    {requested ? ` - Customs asked for ${requested}.` : ' - nothing attached.'}
                                </li>
                            ))}
                        </ul>
                        <Typography.Text type="secondary">
                            Customs does not acknowledge a referral response, and this referral cannot be answered a
                            second time. If a document is missing, Customs can only raise a new referral, order a
                            control, or - before loading - issue a Do Not Load.
                        </Typography.Text>
                    </>
                }
            />
        </CustomsRequestsLayout>
    );
};

export default CustomsRequests;
